import React from 'react';
import hash from 'object-hash';
import useRowGroupSelect from 'components/atoms/tables/smarttables/tableHooks/useRowGroupSelect';
import {
    useTable,
    usePagination,
    useFilters,
    useGlobalFilter,
    useGroupBy,
    useSortBy
} from 'react-table';
import TableView from 'components/atoms/tables/smarttables/TableView';
import { columnFilterFn, fuzzyTextFilterFn, DefaultColumnFilter } from 'components/atoms/tables/smarttables/Filters';

// Our table component
function useTableHook({
    columns, data, initialState = {}, pagination = true,
    globalSearch = true, globalSearchPlaceholder, freeze = false,
    setSearchFnc = null, controlledSearch = false, ViewComponent = null, config = {},
    disableSortBy = false, enableSelection = false
}) {
    const filterTypes = React.useMemo(
        () => ({
            // Add a new fuzzyTextFilterFn filter type.
            fuzzyText: fuzzyTextFilterFn,
            textColumnFilter: columnFilterFn,
            numericColumnFilter: columnFilterFn,
            booleanFilter: columnFilterFn,
            // Or, override the default text filter to use
            // "startWith"
            startsWith: (rows, id, filterValue) => rows.filter((row) => {
                const rowValue = row.values[id];
                return rowValue !== undefined
                    ? String(rowValue)
                        .toLowerCase()
                        .startsWith(String(filterValue).toLowerCase())
                    : true;
            }),
        }),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
            cellProps: { style: { color: '#656158' } },
        }),
        []
    );
    const getViews = (obj) => {
        if (obj.tableProps.state.groupBy.length) {
            return obj.rowsOrPage
                .filter(x => !!x.subRows.length)
                .map((x) => {
                    obj.tableProps.rows = x.subRows;
                    const newConfg = {
                        ...obj,
                        rowsOrPage: x.subRows
                    };
                    return {
                        id: x.subRows[0].groupKey,
                        groupByColumn: obj.tableProps.state.groupBy[0],
                        entity: x.subRows[0].entity,
                        tableRows: newConfg.rowsOrPage,
                        TableView: props => (ViewComponent ? <ViewComponent
                            {...newConfg}
                            {...props}
                            {...config}/> :
                            <TableView {...newConfg} {...props} {...config}/>)
                    };
                }).sort(a => ((a.id === '' || !a.id) ? -1 : 1));
        }
        return [{
            tableRows: obj.tableProps.rows,
            TableView: props => (ViewComponent
                ? <ViewComponent {...obj} {...props} {...config}/>
                : <TableView {...obj} {...props} {...config}/>)
        }];
    };
    const resolveObject = (value, groupKey, entity) => ({
        ...value,
        groupKey,
        entity
    });
    const groupByV2 = (arr, group) => {
        const newObj = arr.reduce((acc, val) => {
            const values = val.original[group];
            const objectHash = hash({ values }, {
                excludeKeys(key) {
                    if (['id'].includes(key)) {
                        return true;
                    }
                    return false;
                },
                unorderedArrays: true
            });
            const groupName = Array.isArray(values) ? values
                .sort((a, b) => (a && a.entityId) - (b && b.entityId))
                .map(x => (x && x.entityId) || x).join(',') : values;
            if (acc[objectHash]) {
                acc[objectHash].push(resolveObject(val, groupName, values));
            } else {
                acc[objectHash] = [resolveObject(val, groupName, values)];
            }
            return acc;
        }, {});
        return Object.values(newObj);
    };
    const tableProps = useTable(
        {
            columns,
            data,
            autoResetSelectedRows: false,
            autoResetFilters: false,
            autoResetSortBy: false,
            autoResetGlobalFilters: false,
            defaultColumn,
            filterTypes,
            initialState,
            disableSortBy,
            groupByFn: groupByV2
        },
        useFilters,
        useGlobalFilter,
        useGroupBy,
        useSortBy,
        useRowGroupSelect(enableSelection),
        usePagination,
    );
    const updateSearch = (val) => {
        if (setSearchFnc) {
            setSearchFnc(val);
        }
        tableProps.setGlobalFilter(val);
    };
    const columnCount = columns.length;
    const rowsOrPage = pagination ? tableProps.page : tableProps.rows;
    const getStickyHeight = () => (globalSearch ? '64px' : '0px');

    const configObj = {
        tableProps,
        columnCount,
        rowsOrPage,
        getStickyHeight,
        updateSearch,
        freeze,
        globalSearchPlaceholder,
        controlledSearch
    };
    return [configObj, getViews(configObj)];
}

export default useTableHook;



// WEBPACK FOOTER //
// ./src/components/atoms/tables/smarttables/useTable.js